import * as Sentry from '@sentry/vue';

const sanitizeHeaders = (headers) => {
  const sensitiveHeaders = ['authorization', 'x-auth-token', 'cookie', 'x-api-key'];

  return Object.entries(headers).reduce((sanitized, [key, headerValue]) => {
    const value = sensitiveHeaders.includes(key.toLowerCase()) ? '[REDACTED]' : headerValue;
    return { ...sanitized, [key]: value };
  }, {});
};

// Sign-in passwords and codes, refresh tokens, and the challenge ID that completes a sign-in,
// never reach Sentry.
const sensitiveBodyKeys = ['password', 'otp', 'refresh_token'];

// Redacts the sensitive keys at any depth.
const redact = (value) => {
  if (Array.isArray(value)) return value.map(redact);
  if (!value || typeof value !== 'object') return value;
  return Object.entries(value).reduce((sanitized, [key, inner]) => ({
    ...sanitized,
    [key]: sensitiveBodyKeys.includes(key.toLowerCase()) ? '[REDACTED]' : redact(inner),
  }), {});
};

const sanitizeBody = (data) => {
  if (typeof data !== 'string') return redact(data);
  try {
    return redact(JSON.parse(data));
  } catch (e) {
    return data;
  }
};

const sanitizeUrl = url => url
  .replace(/(login-challenges\/)[^/?]+/, '$1[REDACTED]')
  .replace(/([?&]refresh_token=)[^&#]+/, '$1[REDACTED]');

const buildErrorContext = (error) => {
  const config = error.config || {};
  const response = error.response || {};
  const url = config.url && sanitizeUrl(config.url);
  return {
    endpoint: url || 'Unknown endpoint',
    method: config.method ? config.method.toUpperCase() : 'Unknown method',
    baseURL: config.baseURL || 'Unknown base URL',
    fullURL: url ? config.baseURL + url : 'Unknown URL',
    requestHeaders: config.headers || {},
    requestBody: config.data ? sanitizeBody(config.data) : null,
    responseStatus: response.status || null,
    responseData: response.data || null,
    timestamp: new Date().toISOString(),
  };
};

export default (error) => {
  try {
    const errorContext = buildErrorContext(error);

    const sanitizedHeaders = sanitizeHeaders(errorContext.requestHeaders);

    Sentry.withScope((scope) => {
      // Set tags for filtering and searching
      scope.setTag('error_type', 'api_error');
      scope.setTag('http_status', errorContext.responseStatus);
      scope.setTag('endpoint', errorContext.endpoint);
      scope.setTag('method', errorContext.method);
      scope.setTag('base_url', errorContext.baseURL);
      scope.setTag('timestamp', errorContext.timestamp);

      scope.setExtra('Request Headers', sanitizedHeaders);


      if (errorContext.requestBody) {
        try {
          const requestBodyJson = JSON.stringify(errorContext.requestBody, null, 2);
          scope.setExtra('Request Body', JSON.parse(requestBodyJson));
        } catch (jsonError) {
          scope.setExtra('Request Body', '[REQUEST_BODY_STRINGIFY_ERROR]');
        }
      }

      if (errorContext.responseData) {
        try {
          const responseDataJson = JSON.stringify(errorContext.responseData, null, 2);
          scope.setExtra('Response Data', responseDataJson);
        } catch (jsonError) {
          scope.setExtra('Response Data', '[RESPONSE_DATA_STRINGIFY_ERROR]');
        }
      }

      scope.setLevel('error');

      Sentry.captureException(error);
    });
  } catch (sentryError) {
    // eslint-disable-next-line no-console
    console.warn('Failed to send error to Sentry:', sentryError);
  }
};




// WEBPACK FOOTER //
// ./src/utils/sentryHelper.js